import { useMemo } from 'react';

import PropTypes from 'prop-types';

import Panel from '@adsk/alloy-react-panel';
import { useUsers } from '../../../providers/RFIsUsersProvider';

import AssignToDropDown from './FilterComponents/AssignToDropDown';
import DropDown from './FilterComponents/components/DropDown';
import CreatedByDropDown from './FilterComponents/CreatedByDropDown';
import { getRFIEnumOptions } from '../../../helpers/enumOptionsHelpers';
import {
  getStartEndDateFromFilter,
  updateFilterDateField,
  getSelectedOptions,
  getUpdateFilters,
  getMultipleSelectFilter,
  sortAlphabeticallyByName,
  filterUsersArray,
} from './FilterComponents/helpers/filterHelpers';
import { StatusDropDown } from '../../../components/Dropdowns/StatusDropDown';
import { useDueDateTimezone, useGetRfiTypes } from '../../../hooks';
import { getAssigneeCompanyName, translate } from '../../../helpers';
import {
  CommonFormField,
  CreatedAtFilterField,
  DueDateFilterField,
  LocationPicker,
  LocationPickerField,
  MultiSelectDropdown,
} from '../../../components';
import { useOxygen } from '../../../providers';
import { AssigneeType } from '../../../sdk';

const nowInUtcIsoString = new Date().toISOString();

const FiltersPanel = ({ filters, setFilters, onClose, open }) => {
  const { data: users } = useUsers();
  const oxygen = useOxygen();
  const { dueDateTimeZone } = useDueDateTimezone(nowInUtcIsoString);

  const updateFilters = (key, value) => {
    const newFilters = { ...filters };

    if (!value || (Array.isArray(value) && value.length === 0)) {
      // Need to delete the entire key from the object because otherwise
      // ReferencePicker will understand it as if the filter exists
      // (even if the value is undefined or null)
      delete newFilters[key];
    } else if (Array.isArray(value) && value.every((item) => typeof item === 'object')) {
      newFilters[key] = value.map((option) => option.value);
    } else {
      newFilters[key] = value;
    }
    setFilters(newFilters);
  };

  const sortedUsers = sortAlphabeticallyByName(filterUsersArray(users));
  const userOptions = sortedUsers.map((user) => ({
    ...user,
    companyName: getAssigneeCompanyName(oxygen, { id: user.autodeskId, type: AssigneeType.User }),
    value: user.autodeskId,
  }));

  const getUsersByIds = (id) => userOptions.find((x) => x.value === id);
  const selectedAssignToValue = filters.assignedTo?.map(getUsersByIds);
  const selectedCreatedByValue = filters.createdBy?.map(getUsersByIds);
  const dueDateValue = getStartEndDateFromFilter(filters.dueDate, dueDateTimeZone);
  const createdAtDateValue = getStartEndDateFromFilter(filters.createdAt);
  const impactOptions = getRFIEnumOptions('scheduleImpact', true);
  const categoryOptions = getRFIEnumOptions('category', true);
  const disciplineOptions = getRFIEnumOptions('discipline', true);
  const priorityOptions = getRFIEnumOptions('priority', true);
  const { data: rfiTypes = [] } = useGetRfiTypes();

  const rfiTypesOptions = useMemo(
    () =>
      rfiTypes
        .filter((rfiType) => rfiType.status === 'active')
        .map((rfiType) => ({
          value: rfiType.id,
          label: rfiType.name,
          key: rfiType.id,
        })),
    [rfiTypes],
  );

  return (
    <Panel title={translate('rpPlugin.filter.filters')} open={open} onClose={onClose}>
      <Panel.Body>
        <StatusDropDown
          selectedStatuses={filters.status}
          onChange={(value) => updateFilters('status', value)}
        />
        <AssignToDropDown
          options={userOptions}
          value={selectedAssignToValue || []}
          subtextGetter={(user) =>
            getAssigneeCompanyName(oxygen, { id: user.autodeskId, type: AssigneeType.User })
          }
          onChange={(value) => updateFilters('assignedTo', getMultipleSelectFilter(value))}
          isLoading={oxygen.loading}
        />
        <DueDateFilterField
          onChange={updateFilterDateField({
            key: 'dueDate',
            updateFilters,
            timeZone: dueDateTimeZone,
          })}
          startDate={dueDateValue.startDate}
          endDate={dueDateValue.endDate}
        />

        <CommonFormField label={translate('rpPlugin.new.rfiType')}>
          <MultiSelectDropdown
            selectedItems={getSelectedOptions(filters.rfiTypeId, rfiTypesOptions)}
            options={rfiTypesOptions}
            onChange={(value) => {
              updateFilters('rfiTypeId', getUpdateFilters(value));
            }}
            placeholder={translate('rpPlugin.filter.rfiType')}
            clearable
          />
        </CommonFormField>

        <LocationPickerField
          selectedLocationIds={filters.locations}
          onChangeLocationIds={(ids) => updateFilters('locations', ids)}
          action={LocationPicker.ACTIONS.FILTER}
        />
        <DropDown
          value={getSelectedOptions(filters.costImpact, impactOptions)}
          options={impactOptions}
          label={translate('rpPlugin.new.costImpact')}
          valueKey="key"
          placeholder={translate('rpPlugin.filter.costImpact')}
          onChange={(value) => {
            updateFilters('costImpact', getUpdateFilters(value));
          }}
        />
        <DropDown
          value={getSelectedOptions(filters.scheduleImpact, impactOptions)}
          options={impactOptions}
          label={translate('rpPlugin.new.scheduleImpact')}
          valueKey="key"
          placeholder={translate('rpPlugin.filter.scheduleImpact')}
          onChange={(value) => updateFilters('scheduleImpact', getUpdateFilters(value))}
        />
        <DropDown
          value={getSelectedOptions(filters.priority, priorityOptions)}
          options={priorityOptions}
          label={translate('rpPlugin.new.priority')}
          valueKey="key"
          placeholder={translate('rpPlugin.filter.priority')}
          onChange={(value) => updateFilters('priority', getUpdateFilters(value))}
        />
        <DropDown
          value={getSelectedOptions(filters.discipline, disciplineOptions)}
          options={disciplineOptions}
          label={translate('rpPlugin.new.discipline')}
          valueKey="key"
          placeholder={translate('rpPlugin.filter.discipline')}
          onChange={(value) => updateFilters('discipline', getUpdateFilters(value))}
        />
        <DropDown
          value={getSelectedOptions(filters.category, categoryOptions)}
          options={categoryOptions}
          label={translate('rpPlugin.new.category')}
          valueKey="key"
          placeholder={translate('rpPlugin.filter.category')}
          onChange={(value) => updateFilters('category', getUpdateFilters(value))}
        />
        <CreatedByDropDown
          options={userOptions}
          value={selectedCreatedByValue || []}
          subtextGetter={(user) =>
            getAssigneeCompanyName(oxygen, { id: user.autodeskId, type: AssigneeType.User })
          }
          onChange={(value) => updateFilters('createdBy', getMultipleSelectFilter(value))}
          isLoading={oxygen.loading}
        />
        <CreatedAtFilterField
          onChange={updateFilterDateField({ key: 'createdAt', updateFilters })}
          startDate={createdAtDateValue.startDate}
          endDate={createdAtDateValue.endDate}
        />
      </Panel.Body>
    </Panel>
  );
};

FiltersPanel.displayName = 'RfisFiltersPanel';

FiltersPanel.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  filters: PropTypes.shape({
    assignedTo: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        type: PropTypes.string,
      }),
    ),
    createdBy: PropTypes.array,
    status: PropTypes.array,
    costImpact: PropTypes.array,
    scheduleImpact: PropTypes.array,
    priority: PropTypes.array,
    discipline: PropTypes.array,
    category: PropTypes.array,
    dueDate: PropTypes.string,
    locations: PropTypes.array,
    rfiTypeId: PropTypes.array,
    createdAt: PropTypes.string,
  }),
  setFilters: PropTypes.func.isRequired,
};

export default FiltersPanel;
